import {ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {BaseRequestComponent} from '../base-request-component';
import {FileRequestService} from './services/file-request.service';
import {FileViewModel} from '@shared/models/file.view-model';
import {EMPTY, Observable, from} from 'rxjs';
import {catchError, concatMap, switchMap, toArray} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {RequestStepFileUploadViewModel} from '../../../view-models/request-step-file-upload-view.model';
import {RequestStepTypeEnum} from '@dto/requests/request-step-type.enum';
import {RequestStepUpdateDto} from '@dto/requests/request-step-update.dto';
import {MessageErrorDetails} from '../../../../messages/entities/message-error-details.model';
import {ErrorModalComponent} from '@shared/components/error-modal/error-modal.component';
import {ModalService} from '@shared/services/modal.service';
import {RequestStepFileDownloadViewModel} from '../../../view-models/request-step-file-download-view.model';
import {FileValidationService} from '@shared/services/file-validation.service';
import {RemovableFileViewModel} from '@shared/models/removable-file.view-model';
import {NewFileViewModel} from '@shared/models/new-file.view-model';
import {FileValidationError} from '@shared/errors/file-validation-error';
import {DomEvent} from '@shared/interfaces/dom-event';
import {FileStepComponent} from '@shared/components/file-step/file-step.component';
import {BlobSasUploadService} from '@shared/services/blob-sas-upload.service';
import {ConfirmDialog} from '@shared/components/confirm-dialog/confirm-dialog.component';

@Component({
    selector: 'app-file-request',
    templateUrl: './file-request.component.html',
    styleUrls: ['./file-request.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FileStepComponent, ConfirmDialog]
})
export class FileRequestComponent extends BaseRequestComponent implements OnInit {

  private readonly destroyRef = inject(DestroyRef);
  private readonly fileRequestService = inject(FileRequestService);
  private readonly fileValidationService = inject(FileValidationService);
  private readonly modalService = inject(ModalService);
  private readonly blobSasUploadService = inject(BlobSasUploadService);

  readonly editable = signal<boolean>(false);

  readonly existingFiles = signal<RemovableFileViewModel[]>([]);
  readonly newFiles = signal<NewFileViewModel[]>([]);
  readonly nextEnabled = signal<boolean>(false);
  readonly pendingDeleteFile = signal<RemovableFileViewModel | null>(null);

  fileTypeWhiteList = FileValidationService.UPLOAD_EXTENSION_WHITELIST.join(',');

  ngOnInit(): void {
    const stepDetail = this.step() as RequestStepFileDownloadViewModel | RequestStepFileUploadViewModel;

    this.existingFiles.set(stepDetail.files.map(f => this.convertFileViewModelToRemovableFileViewModel(f)));
    this.editable.set(this.step().type === RequestStepTypeEnum.FileUpload && !this.request().readonly);
    this.updateNextEnabled();
  }

  private convertFileViewModelToRemovableFileViewModel(file: FileViewModel): RemovableFileViewModel {
    return {
      ...file,
      removed: false
    };
  }

  private updateNextEnabled() {
    const existingFilesCount = this.existingFiles().reduce((count, existingFile) => count + (existingFile.removed ? 0 : 1), 0);
    const totalFileCount = existingFilesCount + this.newFiles().length;
    this.nextEnabled.set(!this.editable() || totalFileCount > 0);
  }

  save() {
    if (!this.nextEnabled()) {
      return;
    }

    if (this.request().readonly) {
      this.next();
      return;
    }

    this.processing = true;
    this.nextEnabled.set(false);

    this.saveInternal().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: updatedRequest => {
        if (updatedRequest !== null) {
          this.updateRequest(updatedRequest);
        }
        this.processing = false;
        this.next();
      },
      error: () => {
        this.processing = false;
        this.updateNextEnabled();
        this.displayErrorModal(this.fileValidationService.genericError());
      }
    });
  }

  private saveInternal(): Observable<RequestStepUpdateDto | null> {
    if (!this.editable()) {
      return this.fileRequestService.submitStep(this.request().id, this.step().id);
    }

    const requestId = this.request().id;
    const stepId = this.step().id;
    const newFiles = this.newFiles();

    if (newFiles.length === 0) {
      // No new files to upload — call submit to advance step in BC.
      return this.fileRequestService.submitStep(requestId, stepId);
    }

    // The upload SAS endpoint now submits metadata to BC before returning the SAS URL, so no
    // separate confirm call is needed. Navigation is driven by next() in the subscribe handler.
    // NOTE: Sequential upload — if a later file fails after earlier files were uploaded,
    // blobs already written to Azure are orphaned (no BC notification for partial batches).
    // This is a known limitation; full cleanup would require a compensating transaction.

    // Line numbers must not collide with files already known to BC. Compute the highest
    // server-assigned lineNo from existing files and allocate new ones sequentially after it.
    const maxExistingLineNo = this.existingFiles().reduce((max, f) => Math.max(max, f.lineNo), 0);

    return from(newFiles).pipe(
      concatMap((nf, i) => {
        const lineNo = maxExistingLineNo + i + 1;
        return this.fileRequestService
          .requestUploadSas(requestId, stepId, {filename: nf.filename, lineNo})
          .pipe(switchMap(sas => {
            if (!nf.file) {
              throw new Error('File payload missing');
            }
            return this.blobSasUploadService.uploadToSasUrl(sas.sasUrl, nf.file);
          }));
      }),
      toArray(),
      switchMap(() => this.fileRequestService.submitStep(requestId, stepId))
    );
  }

  toggleRemoveExistingFile(existingFile: RemovableFileViewModel): void {
    if (existingFile.removed) {
      // Blob is already deleted — toggle-back is a no-op.
      return;
    }
    // Validate first: if removing this file violates a constraint, block before confirming.
    try {
      const hypothetical = this.existingFiles().map(f =>
        f === existingFile ? { ...f, removed: true } : f
      );
      this.fileValidationService.validateFiles(hypothetical, this.newFiles(), []);
    } catch (e) {
      this.displayError(e as Error);
      return;
    }
    // Show confirmation dialog before initiating irreversible server-side delete.
    this.pendingDeleteFile.set(existingFile);
  }

  onConfirmDelete(): void {
    const file = this.pendingDeleteFile();
    if (!file) {
      return;
    }
    this.pendingDeleteFile.set(null);
    this.deleteExistingFileBlob(file);
  }

  onCancelDelete(): void {
    this.pendingDeleteFile.set(null);
  }

  private deleteExistingFileBlob(existingFile: RemovableFileViewModel): void {
    const requestId = this.request().id;
    const stepId = this.step().id;
    const fileIndex = this.existingFiles().indexOf(existingFile);
    if (fileIndex === -1) {
      return;
    }
    const lineNo = existingFile.lineNo;

    // Optimistically mark as removed while deletion is in-flight (immutable update so OnPush detects the change).
    const optimisticFile: RemovableFileViewModel = { ...existingFile, removed: true };
    this.existingFiles.update(files => files.map(f => f === existingFile ? optimisticFile : f));
    this.updateNextEnabled();

    this.fileRequestService
      .requestDeleteSas(requestId, stepId, {filename: existingFile.filename, lineNo})
      .pipe(
        switchMap(sas => this.blobSasUploadService.deleteViaSasUrl(sas.sasUrl)),
        catchError(_err => {
          // Delete failed — roll back via immutable update so user can retry.
          this.existingFiles.update(files => files.map(f => f === optimisticFile ? { ...f, removed: false } : f));
          this.updateNextEnabled();
          this.displayErrorModal(this.fileValidationService.genericError());
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          // Permanently remove from list — blob is gone.
          this.existingFiles.update(files => files.filter(f => f !== optimisticFile));
          this.updateNextEnabled();
        }
      });
  }

  public addNewFiles(event: Event): void {
    const domEvent = event as DomEvent<HTMLInputElement>;
    const fileList = domEvent.target.files;
    const files = Array.from(fileList);

    try {
      this.fileValidationService.validateFiles(this.existingFiles(), this.newFiles(), files);
    } catch (e) {
      this.displayError(e as Error);
      return;
    }

    const viewModels: NewFileViewModel[] = files.map(f => ({
      filename: f.name,
      size: f.size,
      file: f
    }));

    this.newFiles.update(existing => [...existing, ...viewModels]);
    this.updateNextEnabled();
  }

  public removeNewFile(newFile: NewFileViewModel): void {
    this.newFiles.update(files => files.filter(f => f !== newFile));
    this.updateNextEnabled();
  }

  private displayError(e: Error): void {
    if (e instanceof FileValidationError) {
      this.displayErrorModal(e.details);
    } else {
      this.displayErrorModal(this.fileValidationService.genericError());
    }
  }

  onDownloadFile(event: { file: RemovableFileViewModel; lineNo: number }): void {
    const templateId = this.request().originalTemplateId;
    const stepId = this.step().id;

    this.fileRequestService
      .requestDownloadSas(templateId, stepId, { filename: event.file.filename, lineNo: event.lineNo, stepType: this.step().type })
      .pipe(
        switchMap(sas => this.blobSasUploadService.downloadViaSasUrl(sas.sasUrl, event.file.filename)),
        catchError(() => {
          this.displayErrorModal(this.fileValidationService.genericError());
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private displayErrorModal(errorDetails: MessageErrorDetails): void {
    this.modalService.openModal(ErrorModalComponent, errorDetails);
  }
}
